/**
 * Agents API Client
 *
 * API client for AI agent operations in the MATIC system.
 * Handles document suggestions, AI-powered metadata generation, and other agent features.
 */

import {env} from '@matic/config/env';
import {AuthenticatedApiClient} from '@matic/lib/api/authenticated-api-client';

// ============================================================================
// Types
// ============================================================================

/**
 * Suggestion source indicates how the suggestion was derived
 */
export type SuggestionSource = 'content' | 'filename' | 'hybrid' | 'comparison';

/**
 * A single field suggestion with confidence score
 */
export type FieldSuggestion = {
  value: string | undefined;
  confidence: number;
  source: SuggestionSource;
};

/**
 * Response from initial upload suggestion endpoint
 */
export type InitialUploadSuggestion = {
  title?: FieldSuggestion;
  description?: FieldSuggestion;
  documentType?: FieldSuggestion;
  extractionStatus: 'success' | 'partial' | 'failed';
  errors: Array<{field: string; error: string}>;
};

/**
 * A suggested MDM object-record association for an uploaded File.
 *
 * Rendered as a removable "suggested" chip the user confirms — additive on top
 * of whatever preset the upload surface seeds.
 */
export type FileAssociationSuggestion = {
  objectTypeId: string;
  objectRecordId: string;
  objectTypeName: string;
  objectRecordLabel: string;
  confidence: number;
};

/**
 * Response from the generic File upload suggestion endpoint.
 *
 * Mirrors {@link InitialUploadSuggestion} but suggests a File `category`
 * (value is a file category UUID) instead of a document type.
 */
export type FileUploadSuggestion = {
  title?: FieldSuggestion;
  description?: FieldSuggestion;
  category?: FieldSuggestion;
  associations: FileAssociationSuggestion[];
  extractionStatus: 'success' | 'partial' | 'failed';
  errors: Array<{field: string; error: string}>;
};

/**
 * Best-fit category + title for a pending email attachment (existing
 * categories only). `categoryId` is undefined when no category was a confident fit.
 */
export type PendingAttachmentCategorySuggestion = {
  categoryId: string | undefined;
  categoryName: string | undefined;
  confidence: number;
  title: string | undefined;
};

/**
 * An existing active File that shares a pending attachment's content hash.
 *
 * BACKEND FOLLOW-UP (see attachment-grouping.ts #2): add `sizeBytes` +
 * `categoryId`/`categoryName` so the grouped-triage in-library row renders
 * self-contained instead of firing a per-File `useFile` fetch for the category.
 */
export type PendingAttachmentDuplicate = {
  fileId: string;
  name: string;
};

/** Per-attachment triage signal returned by the pending-attachments endpoint. */
export type PendingAttachmentTriageItem = {
  attachmentId: string;
  suggestion: PendingAttachmentCategorySuggestion | undefined;
  duplicateOfFile: PendingAttachmentDuplicate | undefined;
};

/**
 * Response from revision changelog suggestion endpoint
 */
export type RevisionChangelogSuggestion = {
  changelog?: FieldSuggestion;
  contentChanged: boolean;
  extractionStatus: 'success' | 'partial' | 'failed';
  errors: Array<{field: string; error: string}>;
};

// ============================================================================
// Quiz Generation Types
// ============================================================================

export type AiQuizOption = {
  text: string;
};

export type AiQuizQuestion = {
  text: string;
  options: AiQuizOption[];
  // eslint-disable-next-line @typescript-eslint/naming-convention
  correct_answer_index: number;
};

export type GenerateQuizResponse = {
  questions: AiQuizQuestion[];
};

// ============================================================================
// API Response Types (snake_case from backend)
// ============================================================================

type FieldSuggestionResponse = {
  value: string | undefined;
  confidence: number;
  source: string;
};

type InitialUploadSuggestionResponse = {
  title: FieldSuggestionResponse | undefined;
  description: FieldSuggestionResponse | undefined;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  document_type: FieldSuggestionResponse | undefined;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  extraction_status: string;
  errors: Array<{field: string; error: string}>;
};

type AssociationSuggestionResponse = {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  object_type_id: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  object_record_id: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  object_type_name: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  object_record_label: string;
  confidence: number;
};

type FileUploadSuggestionResponse = {
  title: FieldSuggestionResponse | undefined;
  description: FieldSuggestionResponse | undefined;
  category: FieldSuggestionResponse | undefined;
  associations?: AssociationSuggestionResponse[];
  // eslint-disable-next-line @typescript-eslint/naming-convention
  extraction_status: string;
  errors: Array<{field: string; error: string}>;
};

type RevisionSuggestionResponse = {
  changelog: FieldSuggestionResponse | undefined;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  content_changed: boolean;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  extraction_status: string;
  errors: Array<{field: string; error: string}>;
};

type PendingAttachmentSuggestionResponse = {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  category_id: string | undefined;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  category_name: string | undefined;
  confidence: number;
  title: string | undefined;
};

type PendingAttachmentDuplicateResponse = {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  file_id: string;
  name: string;
};

type PendingAttachmentItemResponse = {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  attachment_id: string;
  suggestion: PendingAttachmentSuggestionResponse | undefined;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  duplicate_of_file: PendingAttachmentDuplicateResponse | undefined;
};

type PendingAttachmentSuggestionsResponse = {
  items: PendingAttachmentItemResponse[];
};

// ============================================================================
// Agents API Client
// ============================================================================

/**
 * API client for agents domain operations.
 * Uses token injection pattern - use getAgentsApi() factory function.
 *
 * NOTE: Uses AGENTS_API_URL which may be a separate domain for streaming support.
 * In production, this routes to agents.app.{environment}.matic.systems (REST API).
 * In development, this falls back to API_URL (same domain, http://localhost:8000).
 */
export class AgentsApiClient extends AuthenticatedApiClient {
  constructor(token?: string) {
    super(token, env.AGENTS_API_URL);
  }

  private get basePath(): string {
    return '/api/v1/agents';
  }

  /**
   * Get AI-powered suggestions for a document being uploaded.
   *
   * This endpoint analyzes the document content and filename to suggest:
   * - Document title
   * - Document description/summary
   * - Document type (from tenant's configured types)
   *
   * @param file - The file being uploaded
   * @param filename - Original filename
   * @returns Promise<InitialUploadSuggestion> - Suggestions with confidence scores
   *
   * @example
   * ```typescript
   * const suggestions = await agentsApi.getInitialUploadSuggestions(file, file.name);
   * if (suggestions.title?.confidence >= 0.8) {
   *   // Auto-populate title field
   * }
   * ```
   */
  async getInitialUploadSuggestions(
    file: File,
    filename: string
  ): Promise<InitialUploadSuggestion> {
    const endpoint = `${this.basePath}/suggestions/documents/initial-upload`;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('filename', filename);

    try {
      const apiResponse = await this.post<InitialUploadSuggestionResponse>(
        endpoint,
        formData
      );
      return this.convertSuggestionResponse(apiResponse);
    } catch (error) {
      // Log but don't throw - suggestions are non-critical
      console.error('Failed to get document suggestions:', error);
      // Return empty suggestions on error
      return {
        title: undefined,
        description: undefined,
        documentType: undefined,
        extractionStatus: 'failed',
        errors: [{field: 'api', error: String(error)}],
      };
    }
  }

  /**
   * Get AI-powered suggestions for a generic File being uploaded.
   *
   * Analyzes the file content + filename to suggest a File title, description,
   * and best-fit category. Advisory + best-effort: any failure resolves to an
   * empty suggestion so the upload form keeps working.
   *
   * @param file - The file being uploaded (any type)
   * @param filename - Original filename
   * @returns Promise<FileUploadSuggestion> - Suggestions with confidence scores
   */
  async getFileUploadSuggestions(
    file: File,
    filename: string
  ): Promise<FileUploadSuggestion> {
    const endpoint = `${this.basePath}/suggestions/file-upload`;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('filename', filename);

    try {
      const apiResponse = await this.post<FileUploadSuggestionResponse>(
        endpoint,
        formData
      );
      return this.convertFileSuggestionResponse(apiResponse);
    } catch (error) {
      // Log but don't throw - suggestions are non-critical
      console.error('Failed to get file suggestions:', error);
      return {
        title: undefined,
        description: undefined,
        category: undefined,
        associations: [],
        extractionStatus: 'failed',
        errors: [{field: 'api', error: String(error)}],
      };
    }
  }

  /**
   * Get AI-powered changelog suggestion for a document revision.
   *
   * This endpoint compares the new file with the previous revision and suggests
   * a changelog describing what changed between versions.
   *
   * @param file - The new revision file being uploaded
   * @param previousRevisionId - UUID of the previous revision to compare against
   * @returns Promise<RevisionChangelogSuggestion> - Changelog suggestion with confidence
   *
   * @example
   * ```typescript
   * const suggestion = await agentsApi.getRevisionChangelogSuggestion(file, previousRevisionId);
   * if (suggestion.contentChanged && suggestion.changelog?.confidence >= 0.7) {
   *   // Show AI-generated changelog suggestion
   * }
   * ```
   */
  async getRevisionChangelogSuggestion(
    file: File,
    previousRevisionId: string
  ): Promise<RevisionChangelogSuggestion> {
    const endpoint = `${this.basePath}/suggestions/documents/revision`;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('previous_revision_id', previousRevisionId);

    try {
      const apiResponse = await this.post<RevisionSuggestionResponse>(
        endpoint,
        formData
      );
      return this.convertRevisionSuggestionResponse(apiResponse);
    } catch (error) {
      // Log but don't throw - suggestions are non-critical
      console.error('Failed to get revision changelog suggestion:', error);
      // Return empty suggestion on error
      return {
        changelog: undefined,
        contentChanged: true, // Assume changed on error (safer default)
        extractionStatus: 'failed',
        errors: [{field: 'api', error: String(error)}],
      };
    }
  }

  /**
   * Get on-demand category suggestions + dedup verdicts for pending email
   * attachments. Advisory + best-effort: any failure resolves to an empty list
   * so triage surfaces keep working with a blank picker.
   *
   * @param attachmentIds - Pending attachment ids (server caps the batch).
   * @returns Promise<PendingAttachmentTriageItem[]> - one entry per processed id
   */
  async getPendingAttachmentSuggestions(
    attachmentIds: string[]
  ): Promise<PendingAttachmentTriageItem[]> {
    if (attachmentIds.length === 0) return [];
    const endpoint = `${this.basePath}/suggestions/pending-attachments`;
    try {
      const apiResponse = await this.post<PendingAttachmentSuggestionsResponse>(
        endpoint,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        {attachment_ids: attachmentIds}
      );
      return apiResponse.items.map((item) => ({
        attachmentId: item.attachment_id,
        suggestion: item.suggestion
          ? {
              categoryId: item.suggestion.category_id,
              categoryName: item.suggestion.category_name,
              confidence: item.suggestion.confidence,
              title: item.suggestion.title,
            }
          : undefined,
        duplicateOfFile: item.duplicate_of_file
          ? {
              fileId: item.duplicate_of_file.file_id,
              name: item.duplicate_of_file.name,
            }
          : undefined,
      }));
    } catch (error) {
      console.error('Failed to get pending attachment suggestions:', error);
      return [];
    }
  }

  /**
   * Generate a multiple-choice quiz from a training document using AI.
   *
   * @param documentId - UUID of the document to generate quiz from
   * @returns Promise<GenerateQuizResponse> - Generated quiz questions
   */
  async generateQuiz(
    documentId: string,
    numQuestions?: number
  ): Promise<GenerateQuizResponse> {
    const body: {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      document_id: string;
      // eslint-disable-next-line @typescript-eslint/naming-convention
      num_questions?: number;
    } = {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      document_id: documentId,
    };
    if (numQuestions !== undefined) {
      body.num_questions = numQuestions;
    }

    return this.post<GenerateQuizResponse>(
      `${this.basePath}/quiz/generate`,
      body
    );
  }

  /**
   * Convert API response from snake_case to camelCase
   */
  private convertSuggestionResponse(
    apiResponse: InitialUploadSuggestionResponse
  ): InitialUploadSuggestion {
    return {
      title: apiResponse.title
        ? {
            value: apiResponse.title.value,
            confidence: apiResponse.title.confidence,
            source: apiResponse.title.source as SuggestionSource,
          }
        : undefined,
      description: apiResponse.description
        ? {
            value: apiResponse.description.value,
            confidence: apiResponse.description.confidence,
            source: apiResponse.description.source as SuggestionSource,
          }
        : undefined,
      documentType: apiResponse.document_type
        ? {
            value: apiResponse.document_type.value,
            confidence: apiResponse.document_type.confidence,
            source: apiResponse.document_type.source as SuggestionSource,
          }
        : undefined,
      extractionStatus: apiResponse.extraction_status as
        | 'success'
        | 'partial'
        | 'failed',
      errors: apiResponse.errors,
    };
  }

  /**
   * Convert File upload suggestion API response from snake_case to camelCase
   */
  private convertFileSuggestionResponse(
    apiResponse: FileUploadSuggestionResponse
  ): FileUploadSuggestion {
    return {
      title: apiResponse.title
        ? {
            value: apiResponse.title.value,
            confidence: apiResponse.title.confidence,
            source: apiResponse.title.source as SuggestionSource,
          }
        : undefined,
      description: apiResponse.description
        ? {
            value: apiResponse.description.value,
            confidence: apiResponse.description.confidence,
            source: apiResponse.description.source as SuggestionSource,
          }
        : undefined,
      category: apiResponse.category
        ? {
            value: apiResponse.category.value,
            confidence: apiResponse.category.confidence,
            source: apiResponse.category.source as SuggestionSource,
          }
        : undefined,
      associations: (apiResponse.associations ?? []).map((association) => ({
        objectTypeId: association.object_type_id,
        objectRecordId: association.object_record_id,
        objectTypeName: association.object_type_name,
        objectRecordLabel: association.object_record_label,
        confidence: association.confidence,
      })),
      extractionStatus: apiResponse.extraction_status as
        | 'success'
        | 'partial'
        | 'failed',
      errors: apiResponse.errors,
    };
  }

  /**
   * Convert revision changelog API response from snake_case to camelCase
   */
  private convertRevisionSuggestionResponse(
    apiResponse: RevisionSuggestionResponse
  ): RevisionChangelogSuggestion {
    return {
      changelog: apiResponse.changelog
        ? {
            value: apiResponse.changelog.value,
            confidence: apiResponse.changelog.confidence,
            source: apiResponse.changelog.source as SuggestionSource,
          }
        : undefined,
      contentChanged: apiResponse.content_changed,
      extractionStatus: apiResponse.extraction_status as
        | 'success'
        | 'partial'
        | 'failed',
      errors: apiResponse.errors,
    };
  }
}

// ============================================================================
// Factory Function with Caching
// ============================================================================

const clientCache = new Map<string, AgentsApiClient>();

/**
 * Get an AgentsApiClient instance.
 * Uses caching to reuse client instances per token.
 *
 * @param token - Optional auth token
 * @returns AgentsApiClient instance
 */
export function getAgentsApi(token?: string): AgentsApiClient {
  const cacheKey = token ?? 'no-token';

  if (!clientCache.has(cacheKey)) {
    clientCache.set(cacheKey, new AgentsApiClient(token));
  }

  return clientCache.get(cacheKey)!;
}

// Export default singleton for cases where token is set via context
export const agentsApi = new AgentsApiClient();
